import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Globe } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../context/LanguageContext';
import { offices, regions } from '../data/mockData';

const Presence = () => {
  const { currentLang, t, isRTL } = useLanguage();
  const officeList = offices[currentLang] || offices.fr;
  const regionList = regions[currentLang] || regions.fr;

  return (
    <div className="min-h-screen">
      <Header />
      <main dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Hero Banner */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-[#e8d4e5] via-[#d9c5d6] to-[#c5a3c0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.presencePage.title}
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t.presencePage.description}
              </p>
            </motion.div>
          </div>
        </section>

        {/* World Map Section */}
        <section className="py-20 bg-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg
              viewBox="0 0 1200 600"
              className="w-full h-full"
              preserveAspectRatio="xMidYMid slice"
            >
              {Array.from({ length: 40 }).map((_, row) =>
                Array.from({ length: 80 }).map((_, col) => {
                  const x = col * 15 + 7;
                  const y = row * 15 + 7;
                  const isAfrica = (x > 500 && x < 750 && y > 80 && y < 450);
                  const opacity = isAfrica ? 0.6 : 0;
                  return (
                    <circle
                      key={`${row}-${col}`}
                      cx={x}
                      cy={y}
                      r={2.5}
                      fill="#9B4B8C"
                      opacity={opacity}
                    />
                  );
                })
              )}
            </svg>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{t.presencePage.officesTitle}</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {t.presencePage.officesDescription}
              </p>
            </motion.div>

            <div className="max-w-md mx-auto">
              {officeList.map((office, index) => (
                <motion.div
                  key={office.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-100"
                >
                  <div className="w-12 h-12 bg-[#9B4B8C]/10 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <MapPin className="w-6 h-6 text-[#9B4B8C]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1 text-center">{office.region}</h3>
                  <p className="text-[#9B4B8C] font-medium mb-4 text-center">{office.city}</p>
                  
                  <div className="space-y-3 text-sm">
                    <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">
                        {office.address}<br />
                        {office.addressLine2}
                      </span>
                    </div>
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <a href={`tel:${office.phone}`} className="text-gray-600 hover:text-[#9B4B8C] transition-colors">
                        {office.phone}
                      </a>
                    </div>
                    <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Mail className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex flex-col">
                        <a href={`mailto:${office.email}`} className="text-gray-600 hover:text-[#9B4B8C] transition-colors">
                          {office.email}
                        </a>
                        <a href={`mailto:${office.email2}`} className="text-gray-600 hover:text-[#9B4B8C] transition-colors">
                          {office.email2}
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Regions Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{t.presencePage.regionsTitle}</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {t.presencePage.regionsDescription}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {regionList.map((region, index) => (
                <motion.div
                  key={region.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Globe className="w-6 h-6 text-[#9B4B8C]" />
                    <h3 className="text-xl font-bold text-gray-900">{region.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{region.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {region.countries.map((country) => (
                      <span
                        key={country}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {country}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Presence;
